import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import BBCodeEditor from '../../../components/BBCodeEditor/BBCodeEditor'
import { parseBBCode } from '../../../libraries/BBCode'
import {
  fetchCharacterInventory,
  selectCharacter,
  selectLoading,
  updateCharacterStory,
} from '../ViewInventory/inventorySlice'
import { useSheetReviewContext } from './components/SheetReviewContext'
import './CharacterDetail.css'

const EMPTY_STORY = {
  descPhysical: '',
  descPsychological: '',
  history: '',
}

const STORY_KEYS = ['descPhysical', 'descPsychological', 'history']

function EmptyBlock({ children }) {
  return <p className="char-historia__empty">{children}</p>
}

export default function CharacterHistoria() {
  const { slug } = useParams()
  const dispatch = useDispatch()
  const character = useSelector(selectCharacter)
  const loading = useSelector(selectLoading)

  const {
    canEdit,
    sheetStatus,
    fieldStatusByKey,
    needsChangeKeys,
    loadReview,
  } = useSheetReviewContext()

  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(EMPTY_STORY)

  useEffect(() => {
    if (slug) {
      dispatch(fetchCharacterInventory(slug))
    }
  }, [slug, dispatch])

  useEffect(() => {
    setForm({
      descPhysical: character?.descPhysical || '',
      descPsychological: character?.descPsychological || '',
      history: character?.history || '',
    })
  }, [character?.descPhysical, character?.descPsychological, character?.history])

  const canEditStoryKey = (key) => {
    if (!canEdit) return false
    if (sheetStatus === 'needs_correction') {
      return fieldStatusByKey[key] === 'needs_change' || fieldStatusByKey[key] === 'pending'
    }
    return isEditing
  }

  useEffect(() => {
    if (!canEdit) return
    if (needsChangeKeys.some((k) => STORY_KEYS.includes(k))) {
      setIsEditing(true)
    }
  }, [canEdit, needsChangeKeys])

  const descPhysicalHtml = useMemo(() => parseBBCode(form.descPhysical || ''), [form.descPhysical])
  const descPsychologicalHtml = useMemo(
    () => parseBBCode(form.descPsychological || ''),
    [form.descPsychological]
  )
  const historyHtml = useMemo(() => parseBBCode(form.history || ''), [form.history])

  const handleCancel = () => {
    setForm({
      descPhysical: character?.descPhysical || '',
      descPsychological: character?.descPsychological || '',
      history: character?.history || '',
    })
    setIsEditing(false)
    setError('')
  }

  const handleSave = async () => {
    if (!slug) return
    setSaving(true)
    setError('')
    try {
      await dispatch(
        updateCharacterStory({
          characterName: slug,
          descPhysical: form.descPhysical,
          descPsychological: form.descPsychological,
          history: form.history,
        })
      ).unwrap()
      setIsEditing(false)
      await loadReview()
      await dispatch(fetchCharacterInventory(slug))
    } catch (err) {
      setError(typeof err === 'string' ? err : err?.message || 'No se pudo guardar la historia.')
    } finally {
      setSaving(false)
    }
  }

  if (loading && !character?.name) {
    return (
      <div className="char-historia">
        <div className="char-ficha__loading">
          <span className="char-ficha__loading-spinner" aria-hidden="true" />
          <p>Cargando historia del shinobi…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="char-historia">
      <header className="char-historia__masthead">
        <div className="char-historia__masthead-accent" aria-hidden="true" />
        <div className="char-historia__masthead-body">
          <div className="char-historia__masthead-row">
            <div className="char-historia__masthead-intro">
              <span className="char-historia__masthead-mark" aria-hidden="true">
                史
              </span>
              <div className="char-historia__masthead-text">
                <p className="char-historia__masthead-eyebrow">Crónica del shinobi</p>
                <h2 className="char-historia__title">Historia y biografía</h2>
                <p className="char-historia__subtitle">
                  {character?.name ? (
                    <>
                      Perfil narrativo de <span className="char-historia__name">{character.name}</span>
                    </>
                  ) : (
                    'Perfil narrativo del personaje'
                  )}
                </p>
              </div>
            </div>

            <div className="char-historia__masthead-actions">
              {canEdit && sheetStatus !== 'needs_correction' && !isEditing ? (
                <button type="button" className="char-btn-accent" onClick={() => setIsEditing(true)}>
                  Editar biografía
                </button>
              ) : null}

              {canEdit && isEditing && sheetStatus !== 'needs_correction' ? (
                <div className="char-historia__actions">
                  <button type="button" className="char-btn char-btn--ghost" onClick={handleCancel} disabled={saving}>
                    Cancelar
                  </button>
                  <button type="button" className="char-btn-accent" onClick={handleSave} disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                </div>
              ) : null}

              {canEdit &&
              sheetStatus === 'needs_correction' &&
              STORY_KEYS.some((k) => canEditStoryKey(k)) ? (
                <div className="char-historia__actions">
                  <button type="button" className="char-btn-accent" onClick={handleSave} disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar biografía'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="char-historia__masthead-rule" aria-hidden="true">
            <span className="char-historia__masthead-rule-line" />
            <span className="char-historia__masthead-rule-gem">◆</span>
            <span className="char-historia__masthead-rule-line" />
          </div>
        </div>
      </header>

      {error ? (
        <p className="char-historia__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="char-historia__grid">
        <section className="char-card char-card--section char-historia__block" id="field-descPhysical-block">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Apariencia física</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          {canEditStoryKey('descPhysical') ? (
            <textarea
              className="char-historia__textarea"
              value={form.descPhysical}
              onChange={(e) => setForm((prev) => ({ ...prev, descPhysical: e.target.value }))}
              rows={6}
              placeholder="Altura, complexión, rasgos distintivos, vestimenta…"
            />
          ) : form.descPhysical ? (
            <div
              className="char-historia__bbcode"
              dangerouslySetInnerHTML={{ __html: descPhysicalHtml }}
            />
          ) : (
            <EmptyBlock>Sin descripción física registrada.</EmptyBlock>
          )}
        </section>

        <section className="char-card char-card--section char-historia__block" id="field-descPsychological-block">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Personalidad</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          {canEditStoryKey('descPsychological') ? (
            <textarea
              className="char-historia__textarea"
              value={form.descPsychological}
              onChange={(e) => setForm((prev) => ({ ...prev, descPsychological: e.target.value }))}
              rows={6}
              placeholder="Carácter, motivaciones, virtudes y defectos…"
            />
          ) : form.descPsychological ? (
            <div
              className="char-historia__bbcode"
              dangerouslySetInnerHTML={{ __html: descPsychologicalHtml }}
            />
          ) : (
            <EmptyBlock>Sin descripción psicológica registrada.</EmptyBlock>
          )}
        </section>

        <section
          className="char-card char-card--section char-historia__block char-historia__block--wide"
          id="field-history-block"
        >
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Historia del personaje</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          {canEditStoryKey('history') ? (
            <BBCodeEditor
              value={form.history}
              onChange={(value) => setForm((prev) => ({ ...prev, history: value }))}
              height="280px"
              enableRPCode={false}
              placeholder="Escribe la historia de tu shinobi. Puedes usar BBCode."
            />
          ) : form.history ? (
            <div className="char-historia__bbcode" dangerouslySetInnerHTML={{ __html: historyHtml }} />
          ) : (
            <EmptyBlock>Sin historia registrada.</EmptyBlock>
          )}
        </section>
      </div>
    </div>
  )
}
